import { IOrderViewModel } from "../models/cashdeskModels";
import { humanizePaymentType } from "../../../common/utilities/paymentUtility";
import { dateUtility } from "../../../common/infrastructure/utilities/dateUtility";
import { IStudioSettingsModel } from "../../../common/models/IStudioSettingsModel";
import { classNames } from "primereact/utils";
import { usePermissions } from "../../../common/infrastructure/hooks/usePermissions";
import { endpointsMetadata } from "../../../common/infrastructure/routes/endpointsMetadata";
import { AppDialog } from "../../../common/components/AppDialog/AppDialog";
import styles from "./CashdeskOrderDetailModal.module.scss";

interface ICashdeskOrderDetailModalProps {
  visible: boolean;
  order: IOrderViewModel | null;
  studioSettings?: IStudioSettingsModel;
  onClose: () => void;
  onToggleDeletion: (order: IOrderViewModel) => void;
}

export const CashdeskOrderDetailModal = ({
  visible,
  order,
  studioSettings,
  onClose,
  onToggleDeletion,
}: ICashdeskOrderDetailModalProps) => {
  const { userHavePermissions } = usePermissions();
  const canDelete = userHavePermissions(
    endpointsMetadata.cashdesk.toggleDeletionStatus,
  );

  const formatTime = (utcDate: string) => {
    return dateUtility.formatDateTime(utcDate);
  };

  const header = order ? (
    <div className={styles.header_content}>
      <span className={styles.header_number}>{order.displayNumber}</span>
      <span className={styles.header_date}>
        Продажба · {formatTime(order.createdOn)}
      </span>
    </div>
  ) : undefined;

  return (
    <AppDialog
      header={header}
      visible={visible}
      onHide={onClose}
      className={styles.dialog}
      style={{ width: "400px", maxWidth: "92vw" }}
    >
      {order && (
        <div className={styles.body}>
          {/* Details section */}
          <div className={styles.section}>
            <div className={styles.section_title}>Детайли</div>
            <div className={styles.detail_grid}>
              <div className={styles.detail_row}>
                <span className={styles.detail_label}>Клиент</span>
                <span className={styles.detail_value}>
                  {order.customerName ?? "Анонимна"}
                </span>
              </div>
              <div className={styles.detail_row}>
                <span className={styles.detail_label}>Плащане</span>
                <span className={styles.detail_value}>
                  {humanizePaymentType(order.paymentTypeId)}
                </span>
              </div>
              <div className={styles.detail_row}>
                <span className={styles.detail_label}>Касиер</span>
                <span className={styles.detail_value}>{order.cashierName}</span>
              </div>
              {order.notes && (
                <div className={styles.detail_row}>
                  <span className={styles.detail_label}>Бележка</span>
                  <span className={styles.detail_value}>{order.notes}</span>
                </div>
              )}
            </div>
          </div>

          {/* Products section */}
          <div className={styles.section}>
            <div className={styles.section_title}>Продукти</div>
            <div className={styles.products}>
              {order.items.map((item) => (
                <div key={item.id} className={styles.product_row}>
                  <span className={styles.product_name}>
                    {item.categoryEmojiSnapshot} {item.productNameSnapshot}
                  </span>
                  <span className={styles.product_qty}>× {item.quantity}</span>
                  <span className={styles.product_total}>
                    {item.lineTotal.toFixed(2)} €
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className={styles.totals}>
            {order.discountPercent > 0 && (
              <>
                <div className={styles.totals_row}>
                  <span className={styles.totals_label}>Сума</span>
                  <span className={styles.totals_value}>
                    {order.subTotal.toFixed(2)} €
                  </span>
                </div>
                <div className={styles.totals_row}>
                  <span className={styles.totals_label}>
                    Отстъпка ({order.discountPercent}%)
                  </span>
                  <span className={styles.discount_value}>
                    -{(order.subTotal - order.total).toFixed(2)} €
                  </span>
                </div>
              </>
            )}
            <div className={classNames(styles.totals_row_final, { [styles.totals_row_final_bordered]: order.discountPercent > 0 })}>
              <span className={styles.totals_label_final}>Общо</span>
              <span className={styles.total_value}>
                {order.total.toFixed(2)} €
              </span>
            </div>
            {order.receivedAmount != null && (
              <>
                <div className={styles.totals_row}>
                  <span className={styles.totals_label}>Платено</span>
                  <span className={styles.totals_value}>
                    {order.receivedAmount.toFixed(2)} €
                  </span>
                </div>
                <div className={styles.totals_row}>
                  <span className={styles.totals_label}>Ресто</span>
                  <span className={styles.totals_value}>
                    {(order.changeAmount ?? 0).toFixed(2)} €
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Actions */}
          {canDelete && (
            <button
              className={styles.delete_btn}
              onClick={() => onToggleDeletion(order)}
            >
              {order.isDeleted ? "Възстанови поръчка" : "Изтрий поръчка"}
            </button>
          )}

          <div className={styles.footer}>
            Поръчките не могат да бъдат редактирани след създаване.
          </div>
        </div>
      )}
    </AppDialog>
  );
};
